<template >

    <div class="sdpAssets">

        <!-- page-heading -->
        <page-heading :pageTitle="pageTitle"></page-heading>

        <div class="columns">

            <div class="col-main">

                <v-server-table ref="sdpAssetsTable" name="sdpAssetsTable" class="sdpAssetsTable" :columns="table.tblColumns" :options="table.tblOptions">
                    <!-- before table: FILTER OPTIONS -->
                    <template slot="beforeTable">

                        <div class="boxA" style="display: flex; gap: 5px;">
                            
                            <!-- SUCHE -->
                            <div style="flex: 1;">
                                <div class="control has-icon search" style="min-width:150px;">
                                    <input class="input" type="text" v-model.trim="searchTerm" placeholder="Suchbegriff">
                                    <i class="fa fa-search" aria-hidden="true"></i>
                                </div>
                            </div>

                            <!-- Marke -->
                            <div style="flex: 0;">

                                <form-input
                                    inputType="select"
                                    :showLabel="false"
                                    label="SDP-SELECT_PIM_BRAND"
                                    v-model="filters.brand.value"
                                    selectFieldName="name"
                                    :selectIsSearchable="true"
                                    :selectAllowEmpty="true"
                                    :selectOptions="pimBrands"
                                    :showLabelInOptions="false"
                                    selectOpenDirection="bottom">
                                    
                                </form-input>
                            
                            </div>                  

                            <!-- Kategorie -->
                            <div style="flex: 0;">

                                <form-input
                                    inputType="select"
                                    :showLabel="false"
                                    label="SDP-SELECT_SANOVA_CATEGORY"
                                    v-model="filters.category.value"
                                    selectFieldName="name"
                                    :selectIsSearchable="true"
                                    :selectAllowEmpty="true"
                                    :selectOptions="sanovaCategories"
                                    :showLabelInOptions="false"
                                    selectOpenDirection="bottom">

                                </form-input>
                            </div>

                            <!-- Reset Filter -->
                            <div style="flex: 0;">

                                <span :class="['button reset-filter', isFilterActive ? 'is-blue' : 'is-disabled']"  @click="resetFilters()">
                                <span class="icon is-small">
                                    <i class="icon-ak-reset"></i>
                                </span>
                                </span>

                            </div>

                        </div>

                    </template>

                    <!-- loading indicator -->
                    <template v-if="isLoadingTableData" slot="afterBody">

                        <div class="overlay-loader"></div>
                        <loading-indicator size="36px"></loading-indicator>
                        
                    </template>

                    <!-- uploadFileName -->
                    <template slot="uploadFileName" slot-scope="props">

                        {{ props.row.asset.uploadFilename }}
              
                    </template>

                    <!-- title -->
                    <template slot="assetTitle" slot-scope="props">

                        {{ props.row.title }}
              
                    </template>

                    <!-- brand -->
                    <template slot="pimBrand" slot-scope="props">

                        {{ props.row.pimBrand && props.row.pimBrand.name}}
              
                    </template>

                    <!-- category -->
                    <template slot="sanovaCategory" slot-scope="props">

                        {{ props.row.sanovaCategory && props.row.sanovaCategory.name}}
              
                    </template>

                    <!-- products -->
                    <template slot="products" slot-scope="props">

                        {{ props.row.products && props.row.products.filter(item => item.pimProduct).map(item => item.pimProduct.name).join(', ') }}
              
                    </template>


                    <!-- asset image -->
                    <template slot="asset" slot-scope="props">


                        <div :key="props.row.id">
                            <div v-if="isPdf(props.row.asset)" class="imgWrapper" style="display: flex; align-items: center; justify-content: center; cursor: pointer;" @click.stop="openPreview(props.row.asset)">
                                <i class="fa fa-file-pdf-o" style="color: #ff3860; font-size: 20px;"></i>
                            </div>
                            <div v-else-if="isVideo(props.row.asset)" class="imgWrapper" style="display: flex; align-items: center; justify-content: center; cursor: pointer;" @click.stop="openPreview(props.row.asset)">
                                <i class="fa fa-file-video-o" style="color: #4a4a4a; font-size: 20px;"></i>
                            </div>
                            <div v-else class="imgWrapper lazy-img-fadein" style="cursor: pointer;" v-lazy:background-image="getImagePath(props.row.asset, true)" @click.stop="openPreview(props.row.asset)"></div>
                        </div>

                    </template>

                    <!-- actions -->
                    <template slot="actions" slot-scope="props">

                        <div style="display: flex; gap: 8px;">

                            <span class="icon is-small" @click.stop="assetEdit(props.row)" title="Bearbeiten">
                                <i class="icon-ak-bearbeiten"></i>
                            </span>
                            
                            <pop-confirm 
                                class="icon is-small" 
                                :content="$t('SDP-CONFIRMATION-DELETE_ASSET')" 
                                icon="question-circle-o" 
                                :on-ok="function() { assetDelete(props.row) }" 
                                :ok-text="$t('SDP-BUTTON_REMOVE')" 
                                :cancel-text="$t('SDP-BUTTON_NO')" 
                                :title="$t('SDP-BUTTON_REMOVE')">
                                <i class="icon-ak-entfernen"></i>
                            </pop-confirm>
                        </div>

                    </template>

                </v-server-table>

            </div>

            <div class="col-aside">

                <div class="boxA">
                    <custom-button icon="fa fa-upload" name="SDP-BUTTON_UPLOAD_ASSETS" type="blue" @click.native="uploadModal.isOpen = true"></custom-button>
                </div>

            </div>
        </div>  

        <!-- Upload Modal -->
        <modal 
            :title="$t('SDP-BUTTON_UPLOAD_ASSETS')"
            :is-show="uploadModal.isOpen"
            :backdrop-closable="!uploadModal.isUploading && uploadModal.uploadedFiles.length === 0" 
            :show-footer="true"
            :show-cancel="false"
            :show-ok="false"
            @close="closeUploadModal"
            width="1024"
            transition="fadeDown"
            class="vcenter">
            
            <div style="display: flex; flex-direction: column;">

                <vue-dropzone
                    id="sdpAssetDropzone"
                    ref="sdpAssetDropzone"
                    url="sanovaAsset/uploadAssets"
                    :autoProcessQueue="false"
                    :thumbnailWidth="64"
                    :thumbnailHeight="64" 
                    acceptedFiles="image/jpeg,image/png,image/gif,application/pdf,video/mp4,video/mpeg,.jpg,.jpeg,.png,.gif,.pdf,.mpeg,.mp4"
                    :dictDefaultMessage="'<i class=\'fa fa-cloud-upload\'></i> Dateien hier ablegen oder klicken zum Auswählen'"
                    v-on:vdropzone-file-added="dzFileAdded"
                    v-on:vdropzone-removed-file="dzRemovedFile"
                    dzClass="default">
                </vue-dropzone>

                <div v-if="uploadModal.uploadedFiles.length > 0" class="sdp-upload-list">
                    <div v-for="(f, i) in uploadModal.uploadedFiles" :key="i" class="sdp-upload-list__item" :class="{ 'has-error': uploadModal.validationTriggered && (!f.brand || !f.category) }">
                        <div class="sdp-upload-list__item-preview">
                            <img v-if="f.dataURL" :src="f.dataURL">
                            <i v-else-if="f.file.type === 'application/pdf'" class="fa fa-file-pdf-o icon-pdf"></i>
                            <i v-else-if="f.file.type.match(/video.*/)" class="fa fa-file-video-o icon-video"></i>
                            <i v-else class="fa fa-file icon-generic"></i>

                            <div v-if="f.uploadSuccess === true" class="upload-status is-success">
                                <i class="fa fa-check-circle"></i>
                            </div>
                            <div v-if="f.uploadSuccess === false" class="upload-status is-error">
                                <i class="fa fa-times-circle"></i>
                            </div>
                        </div>
                        <div class="sdp-upload-list__item-inputs" style="flex-wrap: wrap;">
                            <!-- Title -->
                             <div class="fl" style="display: flex; flex-direction: row; gap: 12px; width: 100%;">

                                 <div class="input-col" style="flex: 1;">
                                     <form-input
                                     inputType="text"
                                     :showLabel="true"
                                    label="SDP-ASSET_TITLE"
                                    v-model="f.title">
                                </form-input>
                                </div>
                                <div class="input-col" style="flex: 1;">

                                </div>
                            </div>
            

                            <!-- Marke & Kategorie -->
                            <div class="fl" style="display: flex; flex-direction: row; gap: 12px; width: 100%;">
                                <div class="input-col" style="flex: 1;">
                                    <form-input
                                        inputType="select"
                                        :showLabel="true"
                                        label="SDP-SELECT_PIM_BRAND"
                                        v-model="f.brand"
                                        selectFieldName="name"
                                        :selectIsSearchable="true"
                                        :selectAllowEmpty="true"
                                        :selectOptions="pimBrands"
                                        :showLabelInOptions="false">
                                    </form-input>
                                </div>
                                <div class="input-col" style="flex: 1;">
                                    <form-input
                                        inputType="select"
                                        :showLabel="true"
                                        label="SDP-SELECT_SANOVA_CATEGORY"
                                        v-model="f.category"
                                        selectFieldName="name"
                                        :selectIsSearchable="true"
                                        :selectAllowEmpty="true"
                                        :selectOptions="sanovaCategories"
                                        :showLabelInOptions="false">
                                    </form-input>
                                </div>
                            </div>

                            <!-- Produkte & Gruppen -->
                            <div class="fl" style="display: flex; flex-direction: row; gap: 12px; width: 100%;">
                                <div class="input-col" style="flex: 1;">
                                    <form-input
                                        inputType="select"
                                        :showLabel="true"
                                        label="SDP-SELECT_PRODUCT"
                                        v-model="f.products"
                                        selectFieldName="_formattedName"
                                        :selectOptions="uploadModal.foundProducts"
                                        :selectMultiple="true"
                                        :loading="f.isSearchingProducts"
                                        :selectIsSearchable="true"
                                        :selectInternalSearch="false"
                                        :selectClearOnSelect="false"
                                        :selectCloseOnSelect="false"
                                        selectTrackBy="id"
                                        placeholder="Produkte suchen..."
                                        @search-change="uploadAsyncFindProducts($event, f)"
                                        :showLabelInOptions="false">
                                    </form-input>
                                </div>
    
                                <div class="input-col" style="flex: 1;">
                                    <form-input
                                        inputType="select"
                                        :showLabel="true"
                                        label="SDP-SELECT_GROUP"
                                        v-model="f.groups"
                                        selectFieldName="name"
                                        :selectOptions="uploadModal.foundGroups"
                                        :selectMultiple="true"
                                        :loading="f.isSearchingGroups"
                                        :selectIsSearchable="true"
                                        :selectInternalSearch="false"
                                        :selectClearOnSelect="false"
                                        :selectCloseOnSelect="false"
                                        selectTrackBy="id"
                                        placeholder="Gruppen suchen..."
                                        @search-change="uploadAsyncFindGroups($event, f)"
                                        :showLabelInOptions="false">
                                    </form-input>
                                </div>
                            </div>
                        </div>


                        <!-- Remove Button -->
                        <div class="sdp-upload-list__item-actions">
                            <pop-confirm 
                                :content="$t('SDP-CONFIRMATION-DELETE_ASSET')" 
                                icon="question-circle-o" 
                                :on-ok="function() { removeUploadFile(f) }" 
                                :ok-text="$t('SDP-BUTTON_REMOVE')" 
                                :cancel-text="$t('SDP-BUTTON_NO')" 
                                :title="$t('SDP-BUTTON_REMOVE')">
                                <i class="fa fa-trash btn-delete"></i>
                            </pop-confirm>
                        </div>

                    </div>
                </div>

            </div>

            <div slot="footer">
                <button class="button is-light" :disabled="uploadModal.isUploading" @click="closeUploadModal"><lang labelKey="SDP-BUTTON_CANCEL"></lang></button>
                <button class="button is-primary" :disabled="uploadModal.isUploading || uploadModal.uploadedFiles.length === 0" :class="{'is-loading': uploadModal.isUploading}" @click="uploadAssets"><lang labelKey="SDP-BUTTON_UPLOAD"></lang></button>
            </div>

        </modal>

        <!-- Edit Modal -->
        <modal :title="$t('SDP-EDIT_ASSET')"
            :is-show="editModal.isOpen"
            :backdrop-closable="true"
            :show-footer="true"
            :show-cancel="false"
            :show-ok="false"
            @close="closeEditModal"
            transition="fadeDown"
            width="640"
            class="vcenter">

            <div class="edit-modal">

                <!-- Title -->
                <div class="edit-modal__title-wrapper">
                    <form-input
                        inputType="text"
                        :showLabel="true"
                        :fullWidth="true"
                        label="SDP-ASSET_TITLE"
                        v-model="editModal.title" />
                </div>

                <!-- Marke -->
                <div class="edit-modal__dropdowns-wrapper">
                    <div class="dropdown-item">
                        <form-input
                            inputType="select"
                            :showLabel="true"
                            label="SDP-SELECT_PIM_BRAND"
                            v-model="editModal.brand"
                            selectFieldName="name"
                            :selectIsSearchable="true"
                            :selectAllowEmpty="true"
                            :selectOptions="pimBrands"
                            :showLabelInOptions="false">
                        </form-input>
                    </div>

                    <!-- Kategorie -->
                    <div class="dropdown-item">
                        <form-input
                            inputType="select"
                            :showLabel="true"
                            label="SDP-SELECT_SANOVA_CATEGORY"
                            v-model="editModal.category"
                            selectFieldName="name"
                            :selectIsSearchable="true"
                            :selectAllowEmpty="true"
                            :selectOptions="sanovaCategories"
                            :showLabelInOptions="false">
                        </form-input>
                    </div>
                </div>

                <!-- Produkte & Gruppen -->
                <div class="fl" style="display: flex; flex-direction: column; gap: 12px; margin-top: 12px; margin-bottom: 12px;">
                    <div class="dropdown-item2" style="flex: 1;">
                        <form-input
                            inputType="select"
                            :showLabel="true"
                            label="SDP-SELECT_PRODUCT"
                            v-model="editModal.products"
                            selectFieldName="_formattedName"
                            :selectOptions="editModal.foundProducts"
                            :selectMultiple="true"
                            :loading="editModal.isSearchingProducts"
                            :selectIsSearchable="true"
                            :selectInternalSearch="false"
                            :selectClearOnSelect="false"
                            :selectCloseOnSelect="false"
                            selectTrackBy="id"
                            placeholder="Produkte suchen..."
                            @search-change="asyncFindProducts"
                            :showLabelInOptions="false"
                            :fullWidth="true">
                        </form-input>
                    </div>

                    <div class="dropdown-item2" style="flex: 1;">
                        <form-input
                            inputType="select"
                            :showLabel="true"
                            label="SDP-SELECT_GROUP"
                            v-model="editModal.groups"
                            selectFieldName="name"
                            :selectOptions="editModal.foundGroups"
                            :selectMultiple="true"
                            :loading="editModal.isSearchingGroups"
                            :selectIsSearchable="true"
                            :selectInternalSearch="false"
                            :selectClearOnSelect="false"
                            :selectCloseOnSelect="false"
                            selectTrackBy="id"
                            placeholder="Gruppen suchen..."
                            @search-change="asyncFindGroups"
                            :showLabelInOptions="false"
                            :fullWidth="true">
                        </form-input>
                    </div>
                </div>

                <!-- Asset ersetzen -->
                <div class="edit-modal__replace-wrapper">
                    <label class="replace-label"><lang labelKey="SDP-EDIT_ASSET"></lang></label>

                    <div class="replace-content">
                        
                        <!-- Aktuelles Asset / Preview -->
                        <div v-if="(editModal.row && editModal.row.asset) || editModal.previewDataURL || editModal.newFile" class="preview-container">
                            
                            <!-- PDF Preview (New or Existing) -->
                            <div v-if="(editModal.newFile && editModal.newFile.type === 'application/pdf') || (!editModal.newFile && isPdf(editModal.row && editModal.row.asset))" 
                                 class="imgWrapper pdf-preview preview-box">
                                <i class="fa fa-file-pdf-o icon-pdf"></i>
                                <div v-if="editModal.newFile" class="file-name">{{ editModal.newFile.name }}</div>
                            </div>

                            <!-- Video Preview (New or Existing) -->
                            <div v-else-if="(editModal.newFile && editModal.newFile.type.match(/video.*/)) || (!editModal.newFile && isVideo(editModal.row && editModal.row.asset))" 
                                 class="imgWrapper video-preview preview-box">
                                <i class="fa fa-file-video-o icon-video"></i>
                                <div v-if="editModal.newFile" class="file-name">{{ editModal.newFile.name }}</div>
                            </div>

                            <!-- Image Preview (New or Existing) -->
                            <div v-else 
                                 class="imgWrapper preview-box image-preview" 
                                 :style="{'background-image': 'url(' + (editModal.previewDataURL ? editModal.previewDataURL : getImagePath(editModal.row.asset, true)) + ')'}">
                            </div>
                        </div>

                        <!-- Dropzone zum Ersetzen -->
                        <div class="dropzone-container">
                            <vue-dropzone
                                :key="editDropzoneKey"
                                id="sdpEditDropzone"
                                ref="sdpEditDropzone"
                                 acceptedFiles="image/jpeg,image/png,image/gif,application/pdf,video/mp4,video/mpeg,video/quicktime,.jpg,.jpeg,.png,.gif,.pdf,.mpeg,.mp4,.mov"
                                :autoProcessQueue="false"
                                :maxFiles="1"
                                :dictDefaultMessage="'<i class=\'fa fa-cloud-upload\'></i> Datei ablegen oder klicken zum Auswählen'"
                                v-on:vdropzone-file-added="dzEditFileAdded"
                                dzClass="default">
                            </vue-dropzone>

                        </div>

                    </div>
                </div>

            </div>

            <div slot="footer">
                <button class="button is-light" :disabled="editModal.isSaving" @click="closeEditModal()"><lang labelKey="SDP-BUTTON_CANCEL"></lang></button>
                <button class="button is-primary" :disabled="editModal.isSaving" :class="{'is-loading': editModal.isSaving}" @click="assetSave()"><lang labelKey="SDP-BUTTON_SAVE"></lang></button>
            </div>

        </modal>
        
        <!-- Preview Modal -->
        <modal :title="previewModal.asset ? previewModal.asset.filename : ''"
            :is-show="previewModal.isOpen"
            :backdrop-closable="true"
            :show-footer="false"
            @close="previewModal.isOpen = false"
            width="90%"
            class="vcenter preview-modal-wide">
            
            <div v-if="previewModal.asset" style="display: flex; justify-content: center; align-items: center; min-height: 450px; max-height: 85vh; overflow: auto; background: #fff;">

                <!-- Image -->
                <img v-if="!isPdf(previewModal.asset) && !isVideo(previewModal.asset)" 
                     :src="getImagePath(previewModal.asset, false)" 
                     style="max-width: 100%; max-height: 80vh; object-fit: contain;">
                
                <!-- PDF -->
                <embed v-else-if="isPdf(previewModal.asset)" 
                       :src="getImagePath(previewModal.asset, false)" 
                       type="application/pdf" 
                       width="100%" 
                       style="height: calc(85vh - 60px); min-height: 600px;">
                
                <!-- Video -->
                <video v-else-if="isVideo(previewModal.asset)" 
                       controls 
                       autoplay 
                       style="max-width: 100%; max-height: 80vh;">
                    <source :src="getImagePath(previewModal.asset, false)" :type="getVideoMime(previewModal.asset)">
                    Your browser does not support the video tag.
                </video>
                
            </div>
        </modal>

    </div>        
</template>

<script>
import Vue from 'vue';
import { ServerTable, Event} from 'vue-tables-2';
Vue.use(ServerTable, {}, false, 'bulma');
import { debounce } from "debounce";
import vue2Dropzone from '../utils/dropzoneWrapper';


export default {
    name: 'sanovaAssetManager',

    components: {
        vueDropzone: vue2Dropzone
    },

    data() {
        var self = this;
        return {
            isLoadingTableData: false,
            sanovaCategories: [],
            pimBrands: [],
            searchTerm: null,

            editModal: {
                isOpen: false,
                row: null,
                brand: null,
                category: null,
                title: null,
                replacedAsset: null,
                previewDataURL: null,
                newFile: null,
                isSaving: false,
                products: [],
                groups: [],
                foundProducts: [],
                foundGroups: [],
                isSearchingProducts: false,
                isSearchingGroups: false
            },
            
            previewModal: {
                isOpen: false,
                asset: null
            },

            editDropzoneKey: 0,

            uploadModal: {
                isOpen: false,
                isUploading: false,
                uploadedFiles: [],
                validationTriggered: false,
                foundProducts: [],
                foundGroups: [],
                isSearchingProducts: false,
                isSearchingGroups: false
            },

            filters: {
                search: {
                    type: 'search',
                    value: null,
                    isActive: false
                },
                brand: {
                    type: 'brand',
                    queryName: 'brandId',
                    value: null,
                    isActive: false
                },
                category: {
                    type: 'category',
                    queryName: 'category',
                    value: null,
                    isActive: false
                }
            },  

            pageTitle: 'SAM-ASSETS-HEADER',

            table: {

                tblColumns: ['asset', 'uploadFileName', 'assetTitle', 'pimBrand', 'sanovaCategory', 'products', 'actions'],

                tblOptions: {

                requestFunction(data) {

                    self.isLoadingTableData = true;
                
                    if(data.query != "" && data.query != null) {

                        let query = JSON.parse(data.query);
                        data.query = "";

                        for(let key in query) {

                            let filter = query[key];

                            if(filter.isActive) {

                                if(filter.type == 'category') {
                                    data[filter.queryName] = filter.value ? filter.value.id : null;
                                }

                                if(filter.type == 'brand') {
                                    data[filter.queryName] = filter.value ? filter.value.id : null;
                                }

                                else if(filter.type == 'search') {
                                    data.search = filter.value;
                                }

                            }

                            else if (filter.options) {
                                let selected = filter.options.filter(option => option.selected == true)[0];
                                if(typeof selected != 'undefined') {
                                    if(filter.queryName == 'multiple') {
                                        data[selected.queryName] = selected.id;
                                    } else {
                                        data[filter.queryName] = selected.id;
                                    }
                                }
                            }
                        }
                    }

                    let url = this.$config.api + 'sanovaAsset?include=sanovaAsset.asset,sanovaAsset.pimBrand,sanovaAsset.sanovaCategory,sanovaAsset.products,sanovaAsset.sanovaAssetGroup,sanovaAssetProduct.pimProduct,sanovaAsset.sanovaAssetGroup,sanovaAssetGroupSanovaAsset.sanovaAssetGroup';

                    // provide a comma separated list of ids as “products_ids” and “pimBrandGroup_ids” and they will be saved
                    url += '&limit=' + (self.table.tblOptions.perPage) + '&page=' + (data.page || 1);
                    
            
                    if (data.orderBy) {
                        let orderBy = data.orderBy;
                        if (['pimBrand', 'sanovaCategory'].includes(orderBy)) {
                            orderBy += '.name';
                        }
                        url += '&sortBy=' + encodeURIComponent(orderBy) + (data.ascending === 1 ? '^ASC' : '^DESC');
                    }
                    if (data.search) {
                        url += '&search=' + encodeURIComponent(data.search) + '&searchFields=asset.uploadFilename';
                    }
                    if (data.brandId) {
                        url += '&pimBrand=' + encodeURIComponent(data.brandId);
                    }
                    if (data.category) {
                        url += '&sanovaCategory=' + encodeURIComponent(data.category);
                    }
                    

                    return this.axios.get(url)
                        .then(response => {

                            // if result is empty, return empty array
                            if(response.data.sanovaAsset.length == 0) {
                                return {
                                    data: [],
                                    count: 0
                                }
                            }
                            
                            return {
                                data: response.data.sanovaAsset.items,
                                count: response.data.sanovaAsset.total
                            }


                        }).catch(function (e) {
                            console.log("error ... :", e.message);
                        }.bind(this))
                        .finally(() => {
                            self.isLoadingTableData = false;
                        });

                },

                uniqueKey: 'id',
                perPage: 10,

                sortIcon: {
                    base:'fa',
                    up:'fa-sort-up',
                    down:'fa-sort-down',
                    is:'fa-sort'
                },
                saveState: false,
                preserveState: false,
                filterable: true,
                headings: {
                    asset(h) {
                        return <lang labelKey="SDP-TABLE_HEADING-ASSET"></lang>
                    },
                    uploadFileName(h) {
                        return <lang labelKey="SDP-TABLE_HEADING-NAME"></lang>
                    },
                    assetTitle(h) {
                        return <lang labelKey="SDP-ASSET_TITLE"></lang>
                    },
                    pimBrand(h) {
                        return <lang labelKey="SDP-TABLE_HEADING-BRAND"></lang>
                    },
                    sanovaCategory(h) {
                        return <lang labelKey="SDP-TABLE_HEADING-CATEGORY"></lang>
                    },
                    products(h) {
                        return <lang labelKey="SDP-TABLE_HEADING-PRODUCTS"></lang>
                    },
                    actions(h) {
                        return "";
                    },
                },
                sortable: ['uploadFileName', 'pimBrand', 'sanovaCategory'],
                texts: {
                    first:'Anfang',
                    last:'Ende',
                    filter: 'Filter Ergebnisse:',
                    filterPlaceholder: 'Suche',
                    limit: 'Einträge pro Seite',
                    noResults: 'keine passenden Einträge gefunden',
                    count: '{from} bis {to} von {count} Einträgen|{count} Einträge|ein Eintrag',
                    loading: 'Lade Daten ...'
                },
                pagination: {
                    dropdown: false,
                    chunk: 5,
                    edge: false
                },

                initialPage: self.$store.getters.getCurrentPimTablePage || 1,
                skin: 'table is-striped is-hoverable is-fullwidth',

                columnsClasses: {
                    'asset': 'asset',
                    'select': 'firstCol',
                    'uploadFileName': 'fileName',
                    'title': 'title',
                    'pimBrand': 'brand',
                    'sanovaCategory': 'category',
                    'actions': 'actions'
                },

                }
            },


        }
    },

    created() {
        Promise.all([
            this.getSanovaCategories(),
            this.getPimBrands()
        ]);
    },

    methods: {

        getImagePath(asset, thumb=false) {
            if(asset == null) {
                return "";
            }
            // return "https://images.apopoint.at/" + asset.path + '/' + (thumb != false ? 'thumb/' : '') + asset.filename;
            // return this.$config.cdnHost + "apokat-backend/assets/dam/sanova/" + asset.filename;
             return this.$config.cdnHost + "apokat-backend/assets/dam/sanova/" + (thumb != false ? '150/' : '') + asset.filename;
        },

        isPdf(asset) {
            return asset && asset.filename && asset.filename.toLowerCase().endsWith('.pdf');
        },

        isVideo(asset) {
            return asset && asset.filename && (
                asset.filename.toLowerCase().endsWith('.mp4') || 
                asset.filename.toLowerCase().endsWith('.mov') || 
                asset.filename.toLowerCase().endsWith('.mpeg')
            );
        },

        openPreview(asset) {
            this.previewModal.asset = asset;
            this.previewModal.isOpen = true;
        },

        getVideoMime(asset) {
            if (!asset || !asset.filename) return 'video/mp4';
            const ext = asset.filename.split('.').pop().toLowerCase();
            switch (ext) {
                case 'mp4': return 'video/mp4';
                case 'mov': return 'video/quicktime';
                case 'mpeg': return 'video/mpeg';
                case 'webm': return 'video/webm';
                default: return 'video/mp4';
            }
        },

        dzFileAdded(file) {
            let fileData = { file: file, name: file.name, brand: null, category: null, title: null, dataURL: null, uploadSuccess: null, products: [], groups: [], isSearchingProducts: false, isSearchingGroups: false };
            this.uploadModal.uploadedFiles.push(fileData);
            
            // Wait for Dropzone to generate the thumbnail dataURL
            let checkInterval = setInterval(() => {
                if(file.dataURL) {
                    fileData.dataURL = file.dataURL;
                    clearInterval(checkInterval);
                }
            }, 100);
            
            setTimeout(() => { clearInterval(checkInterval) }, 2000);
        },

        dzRemovedFile(file) {
            this.uploadModal.uploadedFiles = this.uploadModal.uploadedFiles.filter(f => f.file !== file);
        },

        removeUploadFile(f) {
            if (this.$refs.sdpAssetDropzone && this.$refs.sdpAssetDropzone.$refs.sdpAssetDropzone) {
                this.$refs.sdpAssetDropzone.$refs.sdpAssetDropzone.removeFile(f.file);
            } else {
                this.uploadModal.uploadedFiles = this.uploadModal.uploadedFiles.filter(item => item !== f);
            }
        },

        async uploadAssets() {
            this.uploadModal.validationTriggered = true;
            for (const f of this.uploadModal.uploadedFiles) {
                if (!f.brand) {
                    if (this.$notify) {
                        this.$notify.open({ content: this.$t('SDP-ASSET_BRAND_REQUIRED'), type: 'warning' });
                    }
                    return;
                }
                if (!f.category) {
                    if (this.$notify) {
                        this.$notify.open({ content: this.$t('SDP-ASSET_CATEGORY_REQUIRED'), type: 'warning' });
                    }
                    return;
                }
            }

            this.uploadModal.isUploading = true;

            for (const f of this.uploadModal.uploadedFiles) {
                let formData = new FormData();
                formData.append('files[0][file]', f.file);
                if (f.brand) {
                    formData.append('files[0][pimBrand_id]', f.brand.id);
                }
                if (f.category) {
                    formData.append('files[0][sanovaCategory_id]', f.category.id);
                }
                if (f.title) {
                    formData.append('files[0][title]', f.title);
                }

                // Append products as comma-separated IDs
                formData.append('files[0][products_ids]', f.products ? f.products.map(p => p.id).join(',') : '');

                // Append groups as comma-separated IDs
                formData.append('files[0][group_ids]', f.groups ? f.groups.map(g => g.id).join(',') : '');

                try {
                    await this.axios.post(this.$config.api + 'sanovaAsset/uploadAssets', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    f.uploadSuccess = true;
                } catch (e) {
                    console.error('Upload error for file ' + f.name + ':', e);
                    f.uploadSuccess = false;
                    f.errorMsg = e && e.title ? e.title : '';
                }
            }

            if (this.$refs.sdpAssetsTable) {
                this.$refs.sdpAssetsTable.refresh();
            }

            const allSuccess = this.uploadModal.uploadedFiles.every(f => f.uploadSuccess === true);

            if (allSuccess) {
                if (this.$notify) {
                    this.$notify.open({ content: 'Assets erfolgreich hochgeladen', type: 'success' });
                }
                setTimeout(() => {
                    this.closeUploadModal();
                }, 1500);
            } else {
                if (this.$notify) {
                    let errorMessages = this.uploadModal.uploadedFiles
                        .filter(f => f.uploadSuccess === false && f.errorMsg)
                        .map(f => f.errorMsg);
                    
                    // Filter unique messages
                    errorMessages = [...new Set(errorMessages)];
                    
                    let content = 'Einige Assets konnten nicht hochgeladen werden';
                    if (errorMessages.length > 0) {
                        content += ': ' + errorMessages.join(', ');
                    }
                    
                    this.$notify.open({ content: content, type: 'warning' });
                }
            }

            this.uploadModal.isUploading = false;
        },

        dzEditFileAdded(file) {
            this.editModal.newFile = file;
            this.editModal.replacedAsset = file.name;

            // Clear dropzone immediately to show our selection in preview area
            if (file.type.match(/image.*/)) {
                let checkInterval = setInterval(() => {
                    if(file.dataURL) {
                        this.editModal.previewDataURL = file.dataURL;
                        clearInterval(checkInterval);
                        this.editDropzoneKey += 1;
                    }
                }, 100);
                setTimeout(() => { clearInterval(checkInterval); this.editDropzoneKey += 1; }, 2000);
            } else {
                // Non-images (e.g. PDF) won't have dataURL naturally
                this.editModal.previewDataURL = null;
                this.editDropzoneKey += 1;
            }
        },

        assetEdit(row) {
            this.editModal.row = row;
            this.editModal.brand = row.pimBrand ? this.pimBrands.find(b => b.id == row.pimBrand.id) : null;
            this.editModal.category = row.sanovaCategory ? this.sanovaCategories.find(c => c.id == row.sanovaCategory.id) : null;
            this.editModal.title = row.title || null;
            this.editModal.replacedAsset = null;
            this.editModal.previewDataURL = null;
            this.editModal.newFile = null;
            
            // Map products: extract the nested pimProduct objects for the multiselect
            let mappedProducts = row.products
                ? row.products
                    .filter(item => item.pimProduct)
                    .map(item => {
                        const product = { ...item.pimProduct };
                        const name = product.name || product.title || '';
                        const extRef = product.extRef ? ` (#${product.extRef})` : '';
                        product._formattedName = `${name}${extRef}`;
                        return product;
                    })
                : [];
            this.editModal.products = mappedProducts;
            this.editModal.foundProducts = mappedProducts;

            // Map groups: sanovaAssetGroup is an array of join-objects with a nested sanovaAssetGroup property
            let mappedGroups = [];
            if (row.sanovaAssetGroup && row.sanovaAssetGroup.length > 0) {
                mappedGroups = row.sanovaAssetGroup
                    .filter(item => item.sanovaAssetGroup)
                    .map(item => item.sanovaAssetGroup);
            }
            this.editModal.groups = mappedGroups;
            this.editModal.foundGroups = mappedGroups;

            this.editModal.isOpen = true;
        },

        closeUploadModal() {
            if (this.uploadModal.isUploading) return;
            this.uploadModal.isOpen = false;
        },

        closeEditModal() {
            this.editModal.isOpen = false;
            this.editModal.replacedAsset = null;
            this.editModal.previewDataURL = null;
            this.editModal.newFile = null;
            
            // Force recreation of the dropzone to clear it unconditionally
            this.editDropzoneKey += 1;
        },

        assetSave() {

            // check for brand
            if (!this.editModal.brand || this.editModal.brand === '') {
                if (this.$notify) {
                    this.$notify.open({ content: this.$t('SDP-ASSET_BRAND_REQUIRED'), type: 'warning' });
                }
                return;
            }

            // check for category
            if (!this.editModal.category  || this.editModal.category === '') {
                if (this.$notify) {
                    this.$notify.open({ content: this.$t('SDP-ASSET_CATEGORY_REQUIRED'), type: 'warning' });
                }
                return;
            }

            // check for file
            if (!this.editModal.newFile && (!this.editModal.row || !this.editModal.row.asset)) {
                if (this.$notify) {
                    this.$notify.open({ content: this.$t('SDP-ASSET_FILE_REQUIRED'), type: 'warning' });
                }
                return;
            }

            this.editModal.isSaving = true;

            let formData = new FormData();
            formData.append('id', this.editModal.row.id);
            
            if (this.editModal.newFile) {
                formData.append('file', this.editModal.newFile);
            }
            
            formData.append('pimBrand_id', this.editModal.brand ? this.editModal.brand.id : '');
            formData.append('sanovaCategory_id', this.editModal.category ? this.editModal.category.id : '');
            formData.append('title', this.editModal.title ? this.editModal.title : '');

            // Append products as comma-separated IDs
            formData.append('products_ids', this.editModal.products ? this.editModal.products.map(p => p.id).join(',') : '');

            // Append groups as comma-separated IDs (inner sanovaAssetGroup id)
            formData.append('group_ids', this.editModal.groups ? this.editModal.groups.map(g => g.id).join(',') : '');

            this.axios.post(this.$config.api + 'sanovaAsset/replace', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }).then(response => {
                if (this.$notify) {
                    this.$notify.open({content: this.$t('SDP-ASSET_UPDATED_SUCCESS'), type: 'success'});
                }
                if (this.$refs.sdpAssetsTable) {
                    this.$refs.sdpAssetsTable.refresh();
                }
                this.closeEditModal();
            }).catch(error => {
                console.error("Save error: ", error);
                if (this.$notify) {
                    this.$notify.open({content: this.$t('SDP-ASSET_UPDATED_ERROR'), type: 'danger'});
                }
            }).finally(() => {
                this.editModal.isSaving = false;
            });
        },

        assetDelete(row) {
            // Save the current page before deletion so we can restore it
            let currentPage = 1;
            if (this.$refs.sdpAssetsTable) {
                currentPage = this.$refs.sdpAssetsTable.page || 1;
            }

            this.axios.delete(this.$config.api + 'sanovaAsset/' + row.id)
                .then(response => {
                    if (this.$notify) {
                        this.$notify.open({content: this.$t('SDP-ASSET_DELETED_SUCCESS'), type: 'success'});
                    }
                    if (this.$refs.sdpAssetsTable) {
                        this.$refs.sdpAssetsTable.refresh();
                        // Restore the page after refresh; use nextTick so the table has processed the refresh
                        this.$nextTick(() => {
                            if (this.$refs.sdpAssetsTable) {
                                this.$refs.sdpAssetsTable.setPage(currentPage);
                            }
                        });
                    }
                })
                .catch(error => {
                    console.error("Delete error: ", error);
                    if (this.$notify) {
                        this.$notify.open({content: this.$t('SDP-ASSET_DELETED_ERROR'), type: 'danger'});
                    }
                });
        },

        filter() {
            this.$refs.sdpAssetsTable.setFilter(JSON.stringify(this.filters));
        },
        
        performSearch: debounce(function () {
            this.filters.search.value = this.searchTerm;
            this.filters.search.isActive = this.searchTerm != null && this.searchTerm.length > 0;
        }, 500),

        resetFilters() {
            this.searchTerm = null;
            this.filters.brand.value = null;
            this.filters.category.value = null;
        },

        getSanovaCategories() {
            return this.axios.get(this.$config.api + 'sanovaCategory')
                .then(response => {
                    // sort categories by name
                    this.sanovaCategories = response.data.sanovaCategory.sort((a, b) => a.name.localeCompare(b.name));
                }).catch(function (e) {
                    console.log("error ... :", e.message);
                }.bind(this))
            },
        
        getPimBrands() {
            return this.axios.get(this.$config.api + 'pimBrand?filters[sanova]=true')
                .then(response => {
                    // filter out brands with null or empty names and sort by name
                    let pimBrands = 
                    response.data.pimBrand.filter(brand => brand.name !== null && brand.name !== '')
                    /*
                    .map(brand => {
                        brand.name = `${brand.name} (#${brand.id})`;
                        return brand;
                    })
                        */
                    .sort((a, b) => a.name.localeCompare(b.name));

                    this.pimBrands = pimBrands;
       
                }).catch(function (e) {
                    console.log("error ... :", e.message);
                }.bind(this))
        },

        asyncFindProducts: debounce(function(query) {
            if (query == null || query === '') {
                return;
            }
            this.editModal.isSearchingProducts = true;
            
            const payload = {
                query: query,
                limit: 500,
                ascending: 1,
                page: 1,
                byColumn: 0,
                sanovaDownloadPlatform: 1
            };
            
            this.axios.post(this.$config.api + 'pimProduct/query', payload)
                .then(res => {
                    let items = res.data.data || res.data.pimProduct || [];
                    items.forEach(item => {
                        const name = item.name || item.title || '';
                        const extRef = item.extRef ? ` (#${item.extRef})` : '';
                        item._formattedName = `${name}${extRef}`;
                    });
                    this.editModal.foundProducts = items;
                })
                .catch(err => {
                    console.error('Error searching products:', err);
                })
                .finally(() => {
                    this.editModal.isSearchingProducts = false;
                });
        }, 500),

        asyncFindGroups: debounce(function(query) {
            if (query == null || query === '') {
                return;
            }
            this.editModal.isSearchingGroups = true;
            
            this.axios.get(this.$config.api + 'sanovaAssetGroup?search=' + encodeURIComponent(query) + '&searchFields=name')
                .then(res => {
                    let items = [];
                    if (res && res.data) {
                        if (res.data.sanovaAssetGroup) {
                            items = res.data.sanovaAssetGroup.items || res.data.sanovaAssetGroup;
                        } else if (res.data.data) {
                            items = res.data.data;
                        } else if (Array.isArray(res.data)) {
                            items = res.data;
                        }
                    }
                    this.editModal.foundGroups = Array.isArray(items) ? items : [];
                })
                .catch(err => {
                    console.error('Error searching groups:', err);
                })
                .finally(() => {
                    this.editModal.isSearchingGroups = false;
                });
        }, 500),

        uploadAsyncFindProducts: debounce(function(query, f) {
            if (query == null || query === '') {
                return;
            }
            if (f) {
                f.isSearchingProducts = true;
            } else {
                this.uploadModal.isSearchingProducts = true;
            }
            
            const payload = {
                query: query,
                limit: 500,
                ascending: 1,
                page: 1,
                byColumn: 0,
                sanovaDownloadPlatform: 1
            };
            
            this.axios.post(this.$config.api + 'pimProduct/query', payload)
                .then(res => {
                    let items = res.data.data || res.data.pimProduct || [];
                    items.forEach(item => {
                        const name = item.name || item.title || '';
                        const extRef = item.extRef ? ` (#${item.extRef})` : '';
                        item._formattedName = `${name}${extRef}`;
                    });
                    this.uploadModal.foundProducts = items;
                })
                .catch(err => {
                    console.error('Error searching products:', err);
                })
                .finally(() => {
                    if (f) {
                        f.isSearchingProducts = false;
                    } else {
                        this.uploadModal.isSearchingProducts = false;
                    }
                });
        }, 500),

        uploadAsyncFindGroups: debounce(function(query, f) {
            if (query == null || query === '') {
                return;
            }
            if (f) {
                f.isSearchingGroups = true;
            } else {
                this.uploadModal.isSearchingGroups = true;
            }
            
            this.axios.get(this.$config.api + 'sanovaAssetGroup?search=' + encodeURIComponent(query) + '&searchFields=name')
                .then(res => {
                    let items = [];
                    if (res && res.data) {
                        if (res.data.sanovaAssetGroup) {
                            items = res.data.sanovaAssetGroup.items || res.data.sanovaAssetGroup;
                        } else if (res.data.data) {
                            items = res.data.data;
                        } else if (Array.isArray(res.data)) {
                            items = res.data;
                        }
                    }
                    this.uploadModal.foundGroups = Array.isArray(items) ? items : [];
                })
                .catch(err => {
                    console.error('Error searching groups:', err);
                })
                .finally(() => {
                    if (f) {
                        f.isSearchingGroups = false;
                    } else {
                        this.uploadModal.isSearchingGroups = false;
                    }
                });
        }, 500),

        customProductLabel(option) {
            if (!option) return '';
            const name = option.name || option.title || '';
            const extRef = option.extRef ? ` (#${option.extRef})` : '';
            return `${name}${extRef}`;
        }

    },

    mounted() {

    },

    computed: {

        isFilterActive() {
            return (this.searchTerm != null && this.searchTerm !== '') || 
                   (this.filters.brand.value != null) || 
                   (this.filters.category.value != null);
        }

    },

    watch: {

        searchTerm() {
            this.performSearch();
        },

        "filters.brand.value"(val) {
            this.filters.brand.isActive = val != null;
        },

        "filters.category.value"(val) {
            this.filters.category.isActive = val != null;
        },

        "filters": {
            handler(newValue, oldValue) {
                this.filter();
            },
            deep: true
        },

        "uploadModal.isOpen"(isOpen) {
            if (!isOpen) {
                this.uploadModal.validationTriggered = false;
                this.uploadModal.uploadedFiles = [];
                this.uploadModal.isUploading = false;
                if(this.$refs.sdpAssetDropzone && this.$refs.sdpAssetDropzone.$refs.sdpAssetDropzone) {
                    this.$refs.sdpAssetDropzone.$refs.sdpAssetDropzone.removeAllFiles();
                }
            }
        },

        "editModal.isOpen"(isOpen) {
            if (!isOpen) {
                this.editModal.replacedAsset = null;
            }
        }

    },

}
</script>

<style lang="scss" scoped>

.preview-modal-wide ::v-deep .modal-card,
.preview-modal-wide ::v-deep .modal-container {
    width: 90% !important;
    max-width: 90% !important;
}


.sdpAssetsTable {

    .box {
        .column {
            /*padding: 0 4px !important;*/
        }
    }

    ::v-deep > .columns:first-child {
        display: none !important;
    }

    ::v-deep .VueTables__table {
        position: relative !important;
    }

    .overlay-loader {
        position: absolute;
        z-index: 1;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: white;
        opacity: 0.65;
    }

  ::v-deep .v-spinner {
        position: absolute !important;
        z-index: 999;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%);
    }

    ::v-deep .table {

        #VueTables_th--actions  {
            width: 80px;
        }

        #VueTables_th--uploadFileName {
            min-width: 300px;
            width: 300px;
        }

        #VueTables_th--pimBrand {
            min-width: 200px;
            width: 200px;
        }

        #VueTables_th--sanovaCategory {
            min-width: 200px;
            width: 200px;
        }

        td {
            vertical-align: middle;
            cursor: default !important;

            &.asset {
                width: 100px;
                .imgWrapper {
                    background-repeat: no-repeat;
                    background-position: 50% 50% !important;
        
                    background-size: cover;
                    width: 65px;
                    height: 45px;
                    margin: 0;
                    background-color: #FFF;
                    border: 1px solid #efefef;
                    border-radius: 2px;
                }

                .imgWrapper[lazy=loading] {
                    background-size: 30% !important;
                }
            }

            &.actions {
                .icon {
                    cursor: pointer;
                }
            }
        }
  }
}

.sdp-upload-list {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    max-height: 640px;
    overflow-y: auto;
    padding-right: 4px;


    &__item {
        font-size: 0.875rem;
        display: flex;
        align-items: center;
        gap: 16px;
        color: #333;
        flex-direction: row;
        padding: 8px 12px;
        border-radius: 4px;
        background: #fafafa;
        border: 1px solid #e8e8e8;

        &.has-error {
            border-color: #ff3860;
        }

        &-preview {
            width: 64px;
            height: 64px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #eee;
            border-radius: 4px;
            overflow: hidden;
            position: relative;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            .icon-pdf {
                color: #ff3860;
                font-size: 28px;
            }

            .icon-video {
                color: #4a4a4a;
                font-size: 28px;
            }

            .icon-generic {
                color: #999;
                font-size: 24px;
            }

            .upload-status {
                position: absolute;
                inset: 0;
                background: rgba(255,255,255,0.7);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10;

                &.is-success .fa {
                    color: #23d160;
                    font-size: 28px;
                }

                &.is-error .fa {
                    color: #ff3860;
                    font-size: 28px;
                }
            }
        }

        &-inputs {
            display: flex;
            gap: 12px;
            flex: 1;

            .input-col {
                flex: 1;
            }
        }

        &-actions {
            flex-shrink: 0;
            padding-left: 8px;
            display: flex;
            align-items: center;
            justify-content: center;

            .btn-delete {
                cursor: pointer;
                font-size: 1.25rem;
                color: #ff3860;
                opacity: 0.8;
                transition: opacity 0.2s;

                &:hover {
                    opacity: 1;
                }
            }
        }
    }
}

.edit-modal {
    display: flex;
    flex-direction: column;

    &__title-wrapper {
        margin-bottom: 12px;
    }
    
    &__dropdowns-wrapper {
        display: flex;
        gap: 12px;

        .dropdown-item {
            flex: 1;
        }
    }

    &__replace-wrapper {
        margin-top: 8px;

        .replace-label {
            font-size: 0.85rem;
            font-weight: 600;
            margin-bottom: 4px;
            display: block;
        }

        .replace-content {
            display: flex;
            gap: 12px;

            .preview-container {
                flex-shrink: 0;
            }
            #sdpEditDropzone {
                height: 125px !important;
                min-height: 125px !important;
            }
            .dropzone-container {
                flex: 1;
            }
        }
    }
}

.preview-box {
    width: 150px;
    height: 125px;
    border: 1px solid #efefef;
    background-color: #fafafa;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px;
    text-align: center;
    border-radius: 0;

    &.image-preview {
        background-color: #FFF;
        background-size: cover;
        background-position: center !important;
    }

    .icon-pdf {
        font-size: 48px;
        color: #ff3860;
    }

    .icon-video {
        font-size: 48px;
        color: #4a4a4a;
    }

    .file-name {
        font-size: 11px;
        word-break: break-all;
        max-height: 32px;
        overflow: hidden;
        line-height: 1.2;
    }
}

#sdpAssetDropzone ::v-deep .dz-preview {
    display: none !important;
}

#sdpAssetDropzone ::v-deep .dz-message {
    display: flex !important;
}



</style>
